import { useTu } from "@src/appV2/i18n/useTu";
import { formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import { Button, type SxProps, type Theme } from "@mui/material";
import { API_TYPES } from "@src/appV2/api";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { getUserType } from "@src/appV2/PlacementCandidates/utils/helper";
import { useCreateInterview } from "@src/appV2/Placements/api/useCreateInterview";
import { addMinutes } from "date-fns";
import { useCallback, useMemo } from "react";
import { Text } from "../../components/Text";
import type { AdminChatMessage, PlacementChannelMetadata, SuggestInterviewData } from "../types";
import { ChatMessageCard } from "./ChatMessageCard";

interface PlacementApplicationSuggestInterviewProps {
  message: AdminChatMessage;
  channelUrl: string;
  channelMetadata?: PlacementChannelMetadata;
  /**
   * ID of the placement application, needed to book the interview.
   * If not provided, the Book Interview button will be disabled.
   */
  placementApplicationId?: string;
  /**
   * Whether an interview has already been booked for this application.
   */
  hasInterviewBooked?: boolean;
  sx?: SxProps<Theme>;
}

function parseSuggestInterviewData(dataString: string): SuggestInterviewData | undefined {
  try {
    return JSON.parse(dataString) as SuggestInterviewData;
  } catch {
    return undefined;
  }
}

const DEFAULT_INTERVIEW_DURATION_MINUTES = 30;

/**
 * Renders a suggest interview message with a "Book Interview" button.
 * Shown when a worker agrees to an interview at a suggested time.
 */
export function PlacementApplicationSuggestInterview(
  props: PlacementApplicationSuggestInterviewProps
) {
  const { tu } = useTu("chat");

  const {
    message,
    channelUrl,
    channelMetadata,
    placementApplicationId,
    hasInterviewBooked = false,
    sx,
  } = props;

  const { admin, profile, user } = useSession();

  const { mutateAsync: createInterview, isLoading: isCreating } = useCreateInterview({
    onSuccess: () => {
      showSuccessToast(tu("Interview scheduled successfully"));
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data.errors?.[0]?.detail ?? "Unable to schedule interview";
      showErrorToast(errorMessage);
    },
  });

  const { hcpName, placementTitle, facilityName, managingFacilityId } = channelMetadata ?? {};

  // Parse the suggested datetime from the message data
  const suggestedDateTime = useMemo(() => {
    const suggestData = parseSuggestInterviewData(message.message);
    const suggestedDateTimeString = suggestData?.suggestedDateTime;
    return suggestedDateTimeString ? new Date(suggestedDateTimeString) : undefined;
  }, [message.message]);

  const [workplaceId, workerId, placementId] = channelUrl.split("_") as Array<string | undefined>;

  const handleBookInterview = useCallback(async () => {
    if (!placementApplicationId || !suggestedDateTime) {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.PLACEMENT_CANDIDATE_BOOK_INTERVIEW_FROM_CHAT, {
      workplaceId,
      managingWorkplaceId: managingFacilityId,
      workerId,
      placementId,
      userId: user?._id,
      userType: getUserType(profile, admin),
      userEmail: user?.email,
      source: "chatBody",
      suggestedDateTime,
    });

    const dateEnd = addMinutes(suggestedDateTime, DEFAULT_INTERVIEW_DURATION_MINUTES);

    await createInterview({
      relatedResource: { type: API_TYPES.placementApplication, id: placementApplicationId },
      start: suggestedDateTime,
      end: dateEnd,
      bypassInterviewAvailabilityCheck: true,
      notifyInPlacementChatChannel: true,
    });
  }, [
    placementApplicationId,
    suggestedDateTime,
    workplaceId,
    managingFacilityId,
    workerId,
    placementId,
    user?._id,
    user?.email,
    profile,
    admin,
    createInterview,
  ]);

  // If datetime is invalid, show as a simple message
  if (!suggestedDateTime || Number.isNaN(suggestedDateTime.getTime())) {
    return (
      <ChatMessageCard
        subtitle="Worker has agreed to an interview."
        title={tu("Interview Suggested")}
      />
    );
  }

  const timezone = profile?.tmz ?? "UTC";

  // Format date and time with fallback for Storybook/test environments
  let formattedDate: string;

  let formattedTime: string;

  try {
    formattedDate = formatShortDateWithWeekday(suggestedDateTime, { timeZone: timezone });
    formattedTime = formatTime(suggestedDateTime, { timeZone: timezone });
  } catch {
    // Fallback for environments where date-time package doesn't work (e.g., Storybook)
    formattedDate = suggestedDateTime.toLocaleDateString();
    formattedTime = suggestedDateTime.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // Build contextual message if we have metadata
  const contextualMessage =
    hcpName && placementTitle && facilityName
      ? `Great news! It looks like ${hcpName} has agreed to an interview on ${formattedDate} at ${formattedTime}.`
      : `Worker has agreed to an interview on ${formattedDate} at ${formattedTime}.`;

  return (
    <ChatMessageCard
      actions={
        hasInterviewBooked ? (
          <Text semibold variant="body2">
            {tu("Interview scheduled")}
          </Text>
        ) : (
          <Button
            disabled={isCreating || !placementApplicationId}
            size="small"
            variant="contained"
            onClick={() => {
              void handleBookInterview();
            }}
          >
            {tu("Book Interview")}
          </Button>
        )
      }
      subtitle={contextualMessage}
      sx={sx}
      title={tu("Interview Suggested")}
    />
  );
}
